import { useQuery, useQueryClient } from "@tanstack/react-query";

import useGetAccessToken from "components/Login/useGetAccessToken";
import type { Task } from "types/Task";
import { mergeTasksPreservingClientState } from "types/Task";
import { getTasks } from "../methods/getTasks";

export const TASKS_QUERY_KEY = ["cc-db-api", "tasks"];

interface UseTasksQueryOptions<T> {
  select?: (tasks: Task[]) => T;
}

/**
 * Fetches all of the caller's task rows (cross-chat). The in-memory source of
 * truth for the central poller, the Tasks popup, and the completion dot.
 *
 * React Query's automatic focus invalidation is disabled globally (the query
 * client sets `staleTime: Infinity`) so streaming and poller code paths can
 * mutate the cache without RQ overwriting them; a `refetchOnWindowFocus` flag
 * here would be a no-op (ADR 054). Routes that display tasks call `refetch()`
 * from a manual `window` focus listener, gated on any in-flight task write (a
 * focus GET landing mid-commit could resolve with pre-commit server state and
 * overwrite a just-committed terminal status or claim). See `useChatTasks`
 * (which owns the chat route's refetch), `NewChat.tsx`, and
 * `IncognitoChat.tsx` for reference implementations.
 *
 * The `queryFn` reconciles the fetched list against the current cache via
 * `mergeTasksPreservingClientState` so a focus refetch is non-destructive: it
 * preserves a terminal cache status (MCP task status is cache-only, ADR 056, so
 * the server row stays `pending` and must not demote a completed/stalled card)
 * and the client-only `pollExhausted`. Merging in the queryFn, not as
 * `structuralSharing` (which also fires on `setQueryData`), leaves the poller's
 * and sweep's cache-only writes untouched. This complements the in-flight-write
 * guard: the guard covers the cache-only-row window (a just-POSTed row not yet
 * in any GET); the merge covers cache-only fields on rows the GET does return.
 */
export default function useTasksQuery<T = Task[]>(
  opts?: UseTasksQueryOptions<T>
) {
  const getAccessToken = useGetAccessToken();
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: TASKS_QUERY_KEY,
    queryFn: async () => {
      const fresh = await getTasks(getAccessToken);
      const prev = queryClient.getQueryData<Task[]>(TASKS_QUERY_KEY);
      return mergeTasksPreservingClientState(prev, fresh);
    },
    retry: false,
    select: opts?.select,
  });
}
